// Signed-in watch page (DS PlayerScreen, SD35: light, inside the AppShell when the viewer has
// an org; otherwise the public WatchPage layout). §4 #28: GET /watch/{grackle_id}
// ?share= returns WatchOut; a deny (missing or forbidden — indistinguishable per §4.10/AC3) is a
// 404, which renders the same NotFound page a share-page 404 does. FR-EN7: no comments and no
// view list — watching is not recorded anywhere.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Callout, IconButton } from "@grackle/ui";
import { api, ApiFail, deleteErrorMessage, refreshSession } from "../api/client";
import type { GrackleDetail, GrackleVisibility, WatchOut } from "../api/types";
import { AppShell } from "../components/AppShell";
import { resolveActiveOrg } from "../lib/lastOrg";
import { getGrackleTranscript } from "../live/client";
import type { GrackleTranscript } from "../live/types";
import { useIsMobileLayout } from "../lib/mobileLayout";
import MobilePlayerScreen from "../components/mobile/MobilePlayerScreen";
import NotFound from "../pages/NotFound";
import LinkUnavailable from "../pages/LinkUnavailable";
import { useSession } from "../session";
import ConfirmDialog from "../components/ConfirmDialog";
import { PageLoader } from "../components/ui";
import RenameGrackleDialog from "../library/RenameGrackleDialog";
import NotesPanel from "./NotesPanel";
import ShareDialog from "./ShareDialog";
import ShareGate from "./ShareGate";
import { isLinkUnavailable, shareGateFromError, unlockHeaders, type ShareGateInfo } from "./shareGateLogic";
import WatchView, { CopyLinkButton, WatchMeta, WatchPublicHeader } from "./WatchView";
import type { ChaptersResponse } from "./chapters";
import { parseSeekSeconds } from "./seek";
import { useMediaJob } from "./mediaJob";
import { MediaJobNotice, QualityLevelsDialog } from "./QualityLevels";
import "./watch.css";

const POLL_MS = 5000;

export default function WatchPage() {
  const { grackleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const share = searchParams.get("share");
  const navigate = useNavigate();
  const mobileLayout = useIsMobileLayout();
  // Issue #15: no user check for back — it always goes to /orgs. The org only decides the
  // frame: inside the AppShell (the active org, as /orgs would pick it) or the public layout.
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = resolveActiveOrg(orgs, null);

  // `?t=<seconds>` deep link. F2: a transcript search hit links here with ?t= (/g/:id?t=), so
  // every change to it seeks again (another hit for the same grackle). F1: every
  // transcript/notes seek rewrites ?t= so the URL bar stays a shareable link to the same
  // moment; those self-writes are remembered in `selfWrittenT` and skipped, so they don't
  // bounce the player to the rounded second.
  const tParam = searchParams.get("t");
  const selfWrittenT = useRef<string | null>(null);
  const [seekTo, setSeekTo] = useState<number | undefined>(() => parseSeekSeconds(tParam));
  useEffect(() => {
    if (tParam !== null && tParam === selfWrittenT.current) return;
    selfWrittenT.current = null;
    setSeekTo(parseSeekSeconds(tParam));
  }, [tParam]);
  const onSeeked = useCallback(
    (seconds: number) => {
      const t = String(Math.round(seconds));
      selfWrittenT.current = t;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("t", t);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [watch, setWatch] = useState<WatchOut | null>(null);
  const [notFound, setNotFound] = useState(false);
  // F3: an unknown/dead `share` token is 410; a gated link missing a factor is 401 share_locked.
  const [gone, setGone] = useState(false);
  const [gate, setGate] = useState<ShareGateInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [retranscribeOpen, setRetranscribeOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  // SD23/FR-P3b: "Generate quality levels" confirms first (what it does, how long, what it costs).
  const [qualityDialogOpen, setQualityDialogOpen] = useState(false);
  const [transcript, setTranscript] = useState<GrackleTranscript | null>(null);
  // FR-M5b: bumped when the transcript panel regenerates notes, so NotesPanel remounts, reloads
  // and polls the queued generation like its own Regenerate button does.
  const [notesKey, setNotesKey] = useState(0);
  // WatchOut doesn't carry visibility; editors get it from GET /grackles/{id} (as HomePage and
  // LibraryPage do) for the header badge, re-read after the share dialog closes.
  const [visibility, setVisibility] = useState<GrackleVisibility | null>(null);
  const [chapters, setChapters] = useState<ChaptersResponse | null>(null);
  // Bumped when `load` renews an expired session, so requests that went out with the stale
  // token alongside it (chapters) run again.
  const [sessionRenewed, setSessionRenewed] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transcriptPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // F3: held in memory, not state — see useShareWatch for why.
  const unlockTokenRef = useRef<string | null>(null);

  const fetchWatch = useCallback(async (afterRefresh: boolean): Promise<void> => {
    if (!grackleId) return;
    const headers = unlockHeaders(unlockTokenRef.current);
    try {
      const qs = share ? `?share=${encodeURIComponent(share)}` : "";
      const res = await api<WatchOut>("GET", `/watch/${grackleId}${qs}`, undefined, headers);
      if (res.unlock_token) unlockTokenRef.current = res.unlock_token;
      setWatch(res);
      setGate(null);
      setGone(false);
      setLoadError(null);
    } catch (err) {
      const gateInfo = shareGateFromError(err);
      if (gateInfo) {
        setGate(gateInfo);
        setWatch(null);
        setLoadError(null);
      } else if (isLinkUnavailable(err)) {
        setGone(true);
        setGate(null);
        setWatch(null);
        setLoadError(null);
      } else if (err instanceof ApiFail && err.status === 404) {
        // §4.10: an expired access token reads as "signed out", and signed out is a 404 for a
        // private recording. Renew once and retry before calling it missing; for someone who
        // really can't watch, the renewal fails or the retry 404s again, so nothing leaks.
        if (!afterRefresh && (await refreshSession())) {
          setSessionRenewed((n) => n + 1);
          return fetchWatch(true);
        }
        setNotFound(true);
      } else {
        setLoadError(err instanceof ApiFail ? err.message : "Couldn't reach Grackle. Check your connection.");
      }
    }
  }, [grackleId, share]);
  // No arguments: it is also handed to children as onReloadWatch / onQueued.
  const load = useCallback(() => fetchWatch(false), [fetchWatch]);

  const loadTranscript = useCallback(async () => {
    if (!grackleId) return;
    try {
      setTranscript(await getGrackleTranscript(grackleId, share, unlockHeaders(unlockTokenRef.current)));
    } catch {
      // View denial is a 404 on the grackle itself; a failed transcript fetch is not a missing recording.
    }
  }, [grackleId, share]);

  // F12: chapters (manual now; generated once F1 lands). A failure just leaves the empty state.
  useEffect(() => {
    if (!grackleId) return;
    let live = true;
    const qs = share ? `?share=${encodeURIComponent(share)}` : "";
    api<ChaptersResponse>("GET", `/grackles/${grackleId}/chapters${qs}`)
      .then((res) => live && setChapters(res))
      .catch(() => live && setChapters(null));
    return () => {
      live = false;
    };
  }, [grackleId, share, sessionRenewed]);

  useEffect(() => {
    unlockTokenRef.current = null;
    setWatch(null);
    setNotFound(false);
    setGone(false);
    setGate(null);
    setLoadError(null);
    setTranscript(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grackleId, share]);

  useEffect(() => {
    // Re-read once Share, where visibility is changed, closes.
    if (!grackleId || !watch?.can_edit || shareOpen) return;
    let live = true;
    api<GrackleDetail>("GET", `/grackles/${grackleId}`)
      .then((d) => live && setVisibility(d.visibility))
      .catch(() => live && setVisibility(null));
    return () => {
      live = false;
    };
  }, [grackleId, watch?.can_edit, shareOpen]);

  useEffect(() => {
    if (!watch) return;
    if (watch.grackle.transcript_status === "none") {
      setTranscript(null);
      return;
    }
    void loadTranscript();
  }, [watch, loadTranscript]);

  // While a grackle is still uploading/processing, its manifest isn't ready yet — poll until
  // it flips to ready or failed rather than leaving the viewer on a static "processing" page.
  useEffect(() => {
    if (!watch) return;
    const shouldPoll = watch.grackle.status === "uploading" || watch.grackle.status === "processing";
    if (!shouldPoll) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => void load(), POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [watch, load]);

  // The on-demand media jobs (ladder / remux): request, poll, and report — see mediaJob.ts for
  // why the old inline version showed nothing.
  const mediaJob = useMediaJob(grackleId, watch, load);
  const ladderBuilding = mediaJob.busy;
  const hasLadder = watch?.renditions.some((r) => r.name !== "source") ?? false;
  // FR-P14: an uploaded audio file has no picture to remux or ladder, so neither action shows.
  const audioOnly = watch?.grackle.remux_state === "not_applicable";
  // FR-P12: no segments at all, and the server has finished deciding — so this grackle is
  // playing progressively and the remux is the thing that would improve it.
  const needsRemux =
    (watch?.renditions.length ?? 0) === 0 &&
    (watch?.grackle.remux_state === "deferred" || watch?.grackle.remux_state === "failed");

  // FR-P6: poll while a transcribe job is queued or running so the panel flips without a reload.
  useEffect(() => {
    const status = watch?.grackle.transcript_status;
    const shouldPoll = status === "queued" || status === "running";
    if (!shouldPoll) {
      if (transcriptPollRef.current) {
        clearInterval(transcriptPollRef.current);
        transcriptPollRef.current = null;
      }
      return;
    }
    transcriptPollRef.current = setInterval(() => {
      void load();
      void loadTranscript();
    }, POLL_MS);
    return () => {
      if (transcriptPollRef.current) {
        clearInterval(transcriptPollRef.current);
        transcriptPollRef.current = null;
      }
    };
  }, [watch, load, loadTranscript]);

  async function onDelete() {
    setConfirmOpen(true);
  }

  async function onDeleteConfirm() {
    setConfirmOpen(false);
    setDeleting(true);
    setDeleteError(null);
    try {
      await api("DELETE", `/grackles/${grackleId}`);
      navigate(-1);
    } catch (err) {
      setDeleteError(deleteErrorMessage(err, "Couldn't delete this recording."));
      setDeleting(false);
    }
  }

  /** FR-P12/FR-P3b: the two on-demand media jobs, behind one button whose meaning depends on
   * what this grackle is missing. A grackle with no segments needs the remux (`/remux`) before a
   * ladder would even make sense; one that has them can be given more quality options
   * (`/transcode`). Both require FR-O13's permission, and its denial arrives as a plain
   * message — this is the one place in the watch page where a 403 is the honest answer. */
  function requestProcessing(kind: "remux" | "transcode") {
    void mediaJob.start(kind);
  }

  /** `force` re-transcribes a recording that already has a transcript, replacing it. */
  async function requestTranscribe(force = false) {
    if (!grackleId) return;
    setTranscribeError(null);
    setTranscribing(true);
    try {
      await api<{ status: string }>("POST", `/grackles/${grackleId}/transcribe`, force ? { force: true } : undefined);
      await load();
      await loadTranscript();
    } catch (err) {
      setTranscribeError(err instanceof ApiFail ? err.message : "Couldn't start transcription.");
    } finally {
      setTranscribing(false);
    }
  }

  if (notFound) {
    return <NotFound />;
  }

  if (gone) {
    return <LinkUnavailable />;
  }

  // The frame around every state: the AppShell for a member of an org, the public layout
  // (brand header, --surface-app page) for everyone else.
  const frame = (children: ReactNode, owner?: string | null) =>
    user && org ? (
      <AppShell org={org} user={user} section="library" canRecord={user.email_verified ?? false} fullWidth>
        {children}
      </AppShell>
    ) : (
      <div className="watch watch--public">
        <WatchPublicHeader owner={owner} signedIn={Boolean(user)} />
        {children}
      </div>
    );

  if (sessionLoading) {
    return <PageLoader label="Loading recording…" />;
  }

  if (loadError) {
    return frame(
      <div className="watch--center">
        <Callout tone="danger" icon="warning-circle">
          {loadError}
        </Callout>
        <Button variant="secondary" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>,
    );
  }

  if (gate && share) {
    return frame(
      <div className="watch--center">
        <ShareGate
          token={share}
          gate={gate}
          unlockToken={unlockTokenRef.current}
          onUnlocked={(unlockToken) => {
            unlockTokenRef.current = unlockToken;
            void load();
          }}
        />
      </div>,
    );
  }

  if (!watch) {
    return frame(<PageLoader label="Loading recording…" />);
  }

  // PR6 3d/3e: the mobile player + notes layout — see components/mobile/MobilePlayerScreen.
  // Deliberately the one switch point here; editing/sharing/deleting a recording from a phone
  // still goes through the desktop dialogs until PR3's watch page gets its own mobile pass.
  if (mobileLayout) {
    return (
      <MobilePlayerScreen
        grackleId={grackleId ?? ""}
        watch={watch}
        transcript={transcript}
        userId={user?.id}
        onBack={() => void navigate("/orgs")}
        onReloadWatch={load}
      />
    );
  }

  const { grackle } = watch;
  const transcriptBusy = grackle.transcript_status === "queued" || grackle.transcript_status === "running";

  // DS PlayerScreen header: back, title + meta (with the DS's who-can-watch badge, as the library
  // cards show it; Share is where it changes), then Copy link and the one purple Share. The editor's media/transcript jobs and Delete are the ghost row under the
  // video (`actions`), never dropped.
  const topbar = (
    <header className="watch__top">
      <IconButton
        icon="arrow-left"
        label="Back to library"
        variant="outline"
        // Issue #15: a meaningful default rather than browser history. /g/:grackleId is not
        // org-scoped, so back goes to /orgs, which forwards to the active org's start page
        // (OrgChoice) and shows the chooser only to someone who has never picked an org.
        onClick={() => void navigate("/orgs")}
      />
      <div className="watch__head-titles">
        <h1 className="watch__title">{grackle.title}</h1>
        <WatchMeta grackle={grackle} visibility={visibility} />
      </div>
      <div className="watch__actions">
        <CopyLinkButton />
        {watch.can_edit ? (
          <Button icon="share-network" onClick={() => setShareOpen(true)}>
            Share
          </Button>
        ) : null}
      </div>
    </header>
  );

  const actions = watch.can_edit ? (
    <>
      {/* SD25/FR-P12 and SD23/FR-P3b: both media jobs are on-demand actions. Shown only to
          editors, only once the grackle is watchable, and only while there is something left
          to produce. `needsRemux` takes priority — a grackle playing progressively from its
          master gains far more from segments than a segmented one gains from extra rungs.
          SD57: and only while the operator has that kind of processing switched on — both are
          off by default, and an action nobody can take is not shown. */}
      {watch.mux_enabled && grackle.status === "ready" && needsRemux ? (
        <Button
          variant="ghost"
          size="sm"
          icon={ladderBuilding ? "circle-notch" : "sliders-horizontal"}
          onClick={() => requestProcessing("remux")}
          disabled={ladderBuilding}
          title="Prepare this recording for streaming: seekable segments instead of one progressive file"
        >
          {ladderBuilding ? "Preparing for streaming…" : "Prepare for streaming"}
        </Button>
      ) : watch.transcode_enabled && grackle.status === "ready" && !hasLadder && !audioOnly ? (
        <Button
          variant="ghost"
          size="sm"
          icon={ladderBuilding ? "circle-notch" : "sliders-horizontal"}
          onClick={() => setQualityDialogOpen(true)}
          disabled={ladderBuilding}
          title="Encode 360p–1080p quality options so viewers on slow connections can watch smoothly"
        >
          {ladderBuilding ? "Generating quality levels…" : "Generate quality levels"}
        </Button>
      ) : null}
      {watch.can_transcribe &&
      grackle.status === "ready" &&
      (grackle.transcript_status === "none" || grackle.transcript_status === "failed") ? (
        <Button
          variant="ghost"
          size="sm"
          icon={transcribing ? "circle-notch" : "subtitles"}
          onClick={() => void requestTranscribe()}
          disabled={transcribing}
          title="Send this recording's audio to the organization's transcription provider"
        >
          {grackle.transcript_status === "failed" ? "Retry transcription" : "Transcribe"}
        </Button>
      ) : watch.can_transcribe && grackle.status === "ready" && grackle.transcript_status === "ready" ? (
        <Button
          variant="ghost"
          size="sm"
          icon="subtitles"
          onClick={() => setRetranscribeOpen(true)}
          disabled={transcribing}
          title="Transcribe this recording again and replace the current transcript"
        >
          Re-transcribe
        </Button>
      ) : transcriptBusy ? (
        <Button variant="ghost" size="sm" icon="circle-notch" disabled title="Transcription is in progress">
          {grackle.transcript_status === "queued" ? "Transcription queued…" : "Transcribing…"}
        </Button>
      ) : null}
      <span className="watch__actions-spacer" />
      <Button variant="ghost" size="sm" icon="pencil-simple" onClick={() => setRenameOpen(true)}>
        Rename
      </Button>
      <Button variant="danger" size="sm" icon="trash" onClick={() => void onDelete()} disabled={deleting}>
        {deleting ? "Deleting…" : "Delete"}
      </Button>
    </>
  ) : null;

  const errors = [transcribeError, deleteError].filter((e): e is string => Boolean(e));

  const view = (
    <WatchView
      watch={watch}
      onReloadWatch={load}
      transcript={transcript}
      chapters={chapters}
      onChaptersChange={setChapters}
      onTranscriptChange={setTranscript}
      onNotesRegenerate={() => setNotesKey((k) => k + 1)}
      captionsFor={grackleId ? { grackleId, share } : undefined}
      userId={user?.id}
      variant={user && org ? "app" : "public"}
      topbar={user && org ? topbar : <WatchPublicHeader owner={grackle.owner_name} signedIn={Boolean(user)} />}
      actions={actions}
      visibility={visibility}
      initialSeekSeconds={seekTo}
      onSeeked={onSeeked}
      notes={
        grackleId && grackle.status === "ready"
          ? (seek) => (
              <NotesPanel key={notesKey} grackleId={grackleId} share={share} canEdit={watch.can_edit} onSeek={seek} />
            )
          : undefined
      }
      transcriptHint={
        watch.can_edit && watch.can_transcribe
          ? "Transcribe sends audio to this organization's connected provider."
          : undefined
      }
      notices={
        errors.length > 0 || mediaJob.phase.state !== "idle" ? (
          <div className="watch__notices">
            <MediaJobNotice
              kind={mediaJob.kind}
              phase={mediaJob.phase}
              onDismiss={mediaJob.dismiss}
              onRetry={() => mediaJob.kind && requestProcessing(mediaJob.kind)}
            />
            {errors.map((e) => (
              <Callout key={e} tone="danger" icon="warning-circle">
                {e}
              </Callout>
            ))}
          </div>
        ) : null
      }
    />
  );

  return (
    <>
      {user && org ? (
        <AppShell org={org} user={user} section="library" canRecord={user.email_verified ?? false} fullWidth>
          {view}
        </AppShell>
      ) : (
        view
      )}

      {renameOpen && grackleId ? (
        <RenameGrackleDialog
          grackleId={grackleId}
          title={grackle.title}
          onClose={() => setRenameOpen(false)}
          onRenamed={(title) =>
            setWatch((prev) => (prev ? { ...prev, grackle: { ...prev.grackle, title } } : prev))
          }
        />
      ) : null}
      {shareOpen && grackleId ? <ShareDialog grackleId={grackleId} onClose={() => setShareOpen(false)} /> : null}

      <QualityLevelsDialog
        open={qualityDialogOpen}
        sourceHeight={grackle.source_height}
        onClose={() => setQualityDialogOpen(false)}
        onConfirm={() => {
          setQualityDialogOpen(false);
          requestProcessing("transcode");
        }}
      />

      <ConfirmDialog
        open={retranscribeOpen}
        title="Re-transcribe"
        message="Send this recording's audio to the transcription provider again? The new transcript replaces the current one, and speaker names you've set may need setting again."
        confirmLabel="Re-transcribe"
        onClose={() => setRetranscribeOpen(false)}
        onConfirm={() => {
          setRetranscribeOpen(false);
          void requestTranscribe(true);
        }}
      />

      {/* Issue #1: replace window.confirm */}
      <ConfirmDialog
        open={confirmOpen}
        title="Move to trash"
        message={`Move "${grackle.title}" to trash? It's deleted forever after 30 days.`}
        confirmLabel="Move to trash"
        onClose={() => {
          setConfirmOpen(false);
          setDeleting(false);
        }}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
}
